import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  centered: {
    minHeight: '40vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  container: {
    padding: theme.spacing(2)
  },
  pageTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600
  },
  // Section split (e.g. Preschool | Afterschool): a labelled column header.
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.primary.main}`
  },
  sectionItem: {
    marginBottom: theme.spacing(2)
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    background: theme.palette.grey[50]
  },
  filterControl: {
    minWidth: 180
  },
  reasonFilter: {
    minWidth: 240,
    maxWidth: 360,
    '& .MuiSelect-select': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  applyBtn: {
    marginLeft: 'auto'
  },
  teacherFilter: {
    minWidth: 320,
    flex: '1 1 320px'
  },
  teacherOption: {
    width: '100%',
    padding: '2px 0'
  },
  teacherName: {
    fontWeight: 600
  },
  teacherNum: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: '0.8rem'
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4
  },
  subjectPill: {
    height: 20,
    backgroundColor: theme.palette.primary.main,
    color: '#fff'
  },
  centrePill: {
    height: 20,
    backgroundColor: theme.palette.grey[300]
  },
  dashlet: {
    padding: theme.spacing(1.5),
    height: 280,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
    '&:hover': {
      boxShadow: theme.shadows[4]
    }
  },
  chartDashlet: {
    // Trend charts span the full row and grow to fit the SVG instead of the
    // fixed-height table/KPI card.
    height: 'auto',
    minHeight: 320
  },
  dashletTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  dashletBody: {
    flex: 1,
    overflow: 'auto'
  },
  dashletLoading: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  muted: {
    color: theme.palette.text.secondary
  },
  kpiRow: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    gap: theme.spacing(2)
  },
  kpi: {
    textAlign: 'center'
  },
  kpiValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  kpiLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  ratioWrap: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 2)
  },
  ratioValue: {
    fontSize: '2.6rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
    lineHeight: 1
  },
  ratioTotal: {
    color: theme.palette.text.secondary,
    fontWeight: 600
  },
  ratioBarOuter: {
    width: '100%',
    maxWidth: 260,
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.palette.grey[200],
    overflow: 'hidden'
  },
  ratioBarInner: {
    height: '100%',
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.4s ease'
  },
  ratioCaption: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  hbarWrap: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(1),
    height: '100%',
    padding: theme.spacing(1, 1, 1, 0)
  },
  hbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  hbarRowClickable: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  hbarLabel: {
    width: 150,
    flexShrink: 0,
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  hbarTrack: {
    flex: 1,
    height: 22,
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    overflow: 'hidden'
  },
  hbarFill: {
    height: '100%',
    borderRadius: 4,
    minWidth: 2,
    transition: 'width 0.4s ease'
  },
  hbarVal: {
    width: 56,
    flexShrink: 0,
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  previewTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.75rem',
    '& th, & td': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: '4px 6px',
      textAlign: 'left',
      whiteSpace: 'nowrap'
    },
    '& th': {
      color: theme.palette.text.secondary,
      fontWeight: 600
    }
  },
  drillRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  dashletFooter: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  dialogContent: {
    minHeight: '50vh',
    padding: 0
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2)
  },
  dialogHeaderMain: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    minWidth: 0
  },
  dialogHeaderTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  dialogChip: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 600,
    height: 24
  },
  dialogHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0
  },
  dialogCount: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  dialogClose: {
    fontSize: '1rem',
    color: theme.palette.text.secondary
  },
  drillWrap: {
    padding: theme.spacing(1.5, 2, 2)
  },
  drillToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  drillSearch: {
    flex: '1 1 auto',
    maxWidth: 340,
    padding: '8px 12px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: '0.9rem',
    outline: 'none',
    '&:focus': { borderColor: theme.palette.primary.main }
  },
  drillDownload: {
    border: `1px solid ${theme.palette.divider}`,
    background: 'transparent',
    color: theme.palette.text.secondary,
    padding: '7px 14px',
    borderRadius: 8,
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: theme.palette.action.hover }
  },
  // Vertical scroll only — overflow-x:hidden guarantees the table can never
  // scroll sideways or leave a right-side gap.
  drillScroll: {
    maxHeight: 'calc(100vh - 190px)',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  drillTable: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed'
  },
  drillTh: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: theme.palette.background.paper,
    textAlign: 'left',
    verticalAlign: 'top',
    padding: '10px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: '0.5px solid #eeece6',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    '&:last-child': { borderRight: 'none' }
  },
  drillTd: {
    padding: '10px',
    fontSize: '0.82rem',
    color: theme.palette.text.primary,
    borderBottom: '0.5px solid #f0efe9',
    borderRight: '0.5px solid #eeece6',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:last-child': { borderRight: 'none' }
  },
  // Drill pop-up cells wrap instead of cutting off with "…" (owner,
  // 2026-10-23: names, apartments and teachers were unreadable). The
  // spreadsheet-style grid keeps drillTd's one-line cells and its resizing.
  drillTdWrap: {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    overflowWrap: 'anywhere',
    verticalAlign: 'top'
  },
  drillTrAlt: {
    '& td': { backgroundColor: '#f6f7f9' }
  },
  drillEmpty: {
    padding: theme.spacing(5, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  // Grid mode (wide inline data tables): columns size to content and the box
  // scrolls horizontally so long values (e.g. Cadence, SKU) show in full.
  drillScrollGrid: {
    maxHeight: 'calc(100vh - 190px)',
    overflowY: 'auto',
    overflowX: 'auto'
  },
  drillTableGrid: {
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    minWidth: '100%'
  },
  drillThGrid: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: theme.palette.background.paper,
    textAlign: 'left',
    verticalAlign: 'top',
    padding: '10px 14px 10px 10px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: '0.5px solid #eeece6',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:last-child': { borderRight: 'none' }
  },
  // Drag handle on the right edge of each grid-mode header to resize its column.
  resizeHandle: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 8,
    height: '100%',
    cursor: 'col-resize',
    userSelect: 'none',
    '&:hover': { backgroundColor: theme.palette.primary.main, opacity: 0.4 }
  },
  pivotScroll: {
    width: '100%',
    overflowX: 'auto'
  },
  pivotTable: {
    borderCollapse: 'collapse',
    fontSize: '0.8rem',
    width: '100%'
  },
  pivotCorner: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: theme.palette.background.paper,
    textAlign: 'left',
    padding: '8px 10px',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  pivotColHead: {
    padding: '8px 10px',
    textAlign: 'center',
    fontWeight: 600,
    color: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: '0.5px solid #eeece6',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'bottom',
    minWidth: 60
  },
  pivotRowHead: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: theme.palette.background.paper,
    textAlign: 'left',
    padding: '8px 10px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: '0.5px solid #f0efe9',
    whiteSpace: 'nowrap'
  },
  pivotCell: {
    padding: '8px 10px',
    textAlign: 'center',
    color: theme.palette.text.primary,
    borderBottom: '0.5px solid #f0efe9',
    borderLeft: '0.5px solid #eeece6'
  },
  pivotCellClickable: {
    padding: '8px 10px',
    textAlign: 'center',
    color: theme.palette.text.primary,
    borderBottom: '0.5px solid #f0efe9',
    borderLeft: '0.5px solid #eeece6',
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': { outline: `2px solid ${theme.palette.primary.main}` }
  },
  pivotTotal: {
    padding: '8px 10px',
    textAlign: 'center',
    fontWeight: 700,
    color: theme.palette.text.primary,
    borderBottom: '0.5px solid #f0efe9',
    borderLeft: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50]
  },
  pivotTotalClickable: {
    padding: '8px 10px',
    textAlign: 'center',
    fontWeight: 700,
    color: theme.palette.text.primary,
    borderBottom: '0.5px solid #f0efe9',
    borderLeft: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50],
    cursor: 'pointer',
    '&:hover': { outline: `2px solid ${theme.palette.primary.main}` }
  }
}))

export default useStyles
